import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CHANNEL_KIND_LABELS,
  CONVERSATION_STATUS_LABELS,
  PAYMENT_OPTION_LABELS,
  type ChatMessageDTO,
  type ConversationDetailDTO,
  type ConversationRowDTO,
  type ConversationStatus,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Dialog,
  EmptyState,
  Input,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  PageHeader,
  Select,
  Skeleton,
  Switch,
  Textarea,
  useToast,
  type Tone,
} from '@bifeai/ui';
import {
  ArrowLeft,
  Bot,
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Clock,
  Lock,
  Megaphone,
  MessagesSquare,
  MoreHorizontal,
  Send,
  Settings2,
  TriangleAlert,
  Trash2,
  UserCheck,
  UserRound,
  VolumeX,
  X,
  Zap,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { formatDateTime, formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';
import { ORDER_TONES, initials, orderLabel, phoneText, shortTime } from './shared';
import './orders-chat.css';
import { FranchisePrivate } from '../../components/FranchisePrivate';

const FILTERS: { key: string; label: string }[] = [
  { key: 'all', label: 'Todas' },
  { key: 'unread', label: 'Sin leer' },
  { key: 'mine', label: 'Mías' },
  { key: 'human', label: 'Con persona' },
  { key: 'muted', label: 'Silenciadas' },
  { key: 'closed', label: 'Cerradas' },
];
const STATUS_TONES: Record<ConversationStatus, Tone> = { AGENT: 'info', HUMAN: 'warning', MUTED: 'neutral', CLOSED: 'neutral' };

/** Respuestas rápidas: se pegan en el cuadro y se pueden cambiar antes de mandar. */
const QUICK_REPLIES = [
  '¡Hola! Ya te atiendo.',
  'Tu pedido ya está listo, te esperamos.',
  'Gracias por tu compra. ¡Que lo disfrutes!',
  'Hoy no nos queda, ¿te ofrezco otro corte?',
];

/**
 * Bandeja de "Pedidos y chat": conversaciones de todos los canales (hoy WhatsApp), con el agente o con una persona.
 * Se actualiza sola cada pocos segundos.
 */
export function ChatPage() {
  const { privateFranchise } = useSession();
  if (privateFranchise) return <FranchisePrivate title="Chat" what="Los chats" />;
  return <ChatPageContent />;
}

function ChatPageContent() {
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const selected = params.get('c');
  const [filter, setFilter] = useState('all');
  const qc = useQueryClient();
  const toast = useToast();
  const [confirmEmpty, setConfirmEmpty] = useState(false);
  const emptyClosed = useMutation({
    mutationFn: () => api.del<{ deleted: number }>('/orders-chat/conversations/closed'),
    onSuccess: (r) => {
      setConfirmEmpty(false);
      select(null);
      void qc.invalidateQueries({ queryKey: ['orders-chat', 'conversations'] });
      toast.success(r.deleted === 1 ? 'Se borró 1 conversación' : `Se borraron ${r.deleted} conversaciones`);
    },
    onError: (err) => toast.error('No se pudo borrar', errorMessage(err)),
  });
  const [q, setQ] = useState('');
  const list = useQuery({
    queryKey: ['orders-chat', 'conversations', filter, q],
    queryFn: () => api.get<ConversationRowDTO[]>(`/orders-chat/conversations?filter=${filter}${q ? `&q=${encodeURIComponent(q)}` : ''}`),
    refetchInterval: 5_000,
  });
  const select = (id: string | null) => setParams(id ? { c: id } : {}, { replace: true });
  // El chat ocupa el alto de la ventana, como WhatsApp Web: cada columna scrollea por dentro y la página queda quieta.
  const box = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const fit = () =>
      el.style.setProperty('--bf-chat-h', `${Math.max(380, window.innerHeight - (el.getBoundingClientRect().top + window.scrollY) - 12)}px`);
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  return (
    <>
      <PageHeader
        title="Chat"
        actions={
          (can('settings.manage') || can('franchise.accounts')) && (
            <Link to="/pedidos/configuracion" className="bf-btn bf-btn--sm">
              <Settings2 size={16} aria-hidden /> Configurar agente
            </Link>
          )
        }
      />
      <div ref={box} className={`bf-chat${selected ? ' has-selection' : ''}`}>
        <div className="bf-chat-list">
          <div style={{ padding: 12, display: 'grid', gap: 8, borderBottom: '1px solid var(--bf-line)' }}>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar nombre o teléfono" aria-label="Buscar conversación" />
            {/* Desplegable: deja lugar para más conversaciones (y para cuando lleguen más canales). */}
            <Select
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                select(null); // al cambiar de lista, no queda a la vista una conversación de otra
              }}
              aria-label="Mostrar"
            >
              {FILTERS.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </Select>
            {filter === 'closed' && can('settings.manage') && (list.data?.length ?? 0) > 0 && (
              <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setConfirmEmpty(true)}>
                Borrar todas las cerradas
              </Button>
            )}
          </div>
          <Dialog
            open={confirmEmpty}
            onOpenChange={(o) => !emptyClosed.isPending && setConfirmEmpty(o)}
            title="¿Borrar todas las conversaciones cerradas?"
            description={`${(list.data?.length ?? 0) === 1 ? 'Se borra 1 conversación' : `Se borran ${list.data?.length ?? 0} conversaciones`} con todos sus mensajes. Los pedidos no se borran. No se puede deshacer.`}
          >
            <div className="bf-form-actions">
              <Button onClick={() => setConfirmEmpty(false)} disabled={emptyClosed.isPending}>
                Cancelar
              </Button>
              <Button variant="danger" loading={emptyClosed.isPending} onClick={() => emptyClosed.mutate()}>
                Borrar todas
              </Button>
            </div>
          </Dialog>
          {list.isPending && (
            <div style={{ padding: 16, display: 'grid', gap: 12 }}>
              <Skeleton />
              <Skeleton />
              <Skeleton />
            </div>
          )}
          {list.data?.length === 0 && (
            <EmptyState
              icon={<MessagesSquare />}
              title={filter === 'all' && !q ? 'No hay conversaciones abiertas' : 'No hay conversaciones en esta lista'}
              text={
                filter === 'all' && !q
                  ? 'Cuando un cliente escriba al WhatsApp de la carnicería, aparece acá. Las cerradas están en «Cerradas».'
                  : undefined
              }
            />
          )}
          <ul className="bf-chat-rows">
            {list.data?.map((c) => (
              <li key={c.id}>
                <button type="button" className="bf-chat-row" aria-current={c.id === selected ? 'true' : undefined} onClick={() => select(c.id)}>
                  <span className="bf-chat-avatar" aria-hidden>
                    {initials(c.contactName)}
                    <ChannelIcon kind={c.channelKind} />
                  </span>
                  <span className="bf-chat-row-main">
                    <span className="bf-chat-row-top">
                      <strong>{c.contactName}</strong>
                      <span className="bf-cell-sub">{shortTime(c.lastMessageAt)}</span>
                    </span>
                    <span className="bf-chat-row-preview">{c.lastPreview ?? 'Sin mensajes'}</span>
                    <span className="bf-chat-row-tags">
                      <Badge tone={STATUS_TONES[c.status]}>{c.status === 'AGENT' ? 'Agente' : CONVERSATION_STATUS_LABELS[c.status]}</Badge>
                      {c.openOrderNumber && <Badge tone="success">Pedido #{c.openOrderNumber}</Badge>}
                      {c.fromAd && <Badge tone="info">Anuncio</Badge>}
                    </span>
                  </span>
                  {c.unreadCount > 0 && (
                    <span className="bf-chat-unread" aria-label={`${c.unreadCount} sin leer`}>
                      {c.unreadCount}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
        {selected ? (
          <Thread key={selected} id={selected} onBack={() => select(null)} />
        ) : (
          <div className="bf-chat-empty">
            <EmptyState
              icon={<MessagesSquare />}
              title="Elegí una conversación"
              text="Vas a ver los mensajes, los datos del cliente y sus pedidos."
            />
          </div>
        )}
      </div>
    </>
  );
}

function StatusIcon({ m }: { m: ChatMessageDTO }) {
  if (m.direction === 'IN' || m.internal) return null;
  if (m.status === 'FAILED') return <TriangleAlert size={13} aria-label="No se pudo enviar" style={{ color: 'var(--bf-danger)' }} />;
  if (m.status === 'QUEUED') return <Clock size={13} aria-label="En cola" />;
  if (m.status === 'READ') return <CheckCheck size={13} aria-label="Leído" style={{ color: 'var(--bf-celeste)' }} />;
  if (m.status === 'DELIVERED') return <CheckCheck size={13} aria-label="Entregado" />;
  return <Check size={13} aria-label="Enviado" />;
}

function Thread({ id, onBack }: { id: string; onBack: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [text, setText] = useState('');
  const [internal, setInternal] = useState(false);
  // Cliente y pedidos se pliegan a una barrita para darle lugar al chat. Cada uno se abre por separado;
  // tocar el que está abierto lo cierra. Se recuerda la elección.
  const [side, setSide] = useState<'cliente' | 'pedidos' | null>(() => {
    try {
      const v = localStorage.getItem('bf-chat-side');
      return v === 'cliente' || v === 'pedidos' ? v : null;
    } catch {
      return null;
    }
  });
  const showSide = (tab: 'cliente' | 'pedidos' | null) => {
    setSide(tab);
    try {
      localStorage.setItem('bf-chat-side', tab ?? 'closed');
    } catch {
      /* sin almacenamiento: queda como está en esta sesión */
    }
  };
  const [quickOpen, setQuickOpen] = useState(false);
  const msgs = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const seen = useRef(0);
  const [unseen, setUnseen] = useState(false);
  const detail = useQuery({
    queryKey: ['orders-chat', 'conversation', id],
    queryFn: () => api.get<ConversationDetailDTO>(`/orders-chat/conversations/${id}`),
    refetchInterval: 4_000,
  });
  const count = detail.data?.messages.length ?? 0;
  // Al abrir la conversación queda leída: la campanita y la bandeja se actualizan en el momento.
  const loaded = Boolean(detail.data);
  useEffect(() => {
    if (!loaded) return;
    void qc.invalidateQueries({ queryKey: ['orders-chat', 'summary'] });
    void qc.invalidateQueries({ queryKey: ['orders-chat', 'conversations'] });
  }, [loaded, id, qc]);
  // Baja al último mensaje solo dentro de la caja del chat, y solo si ya estabas abajo (o lo mandaste vos).
  // Si estabas leyendo más arriba, no te mueve: aparece "Mensajes nuevos".
  const lastAuthor = detail.data?.messages.at(-1)?.author;
  const toBottom = () => {
    const el = msgs.current;
    if (el) el.scrollTop = el.scrollHeight;
    setUnseen(false);
  };
  useLayoutEffect(() => {
    if (!count) return;
    if (seen.current === 0 || atBottom.current || lastAuthor === 'STAFF') toBottom();
    else if (count > seen.current) setUnseen(true);
    seen.current = count;
  }, [count, lastAuthor]);
  const refresh = (d: ConversationDetailDTO) => {
    qc.setQueryData(['orders-chat', 'conversation', id], d);
    void qc.invalidateQueries({ queryKey: ['orders-chat', 'conversations'] });
    void qc.invalidateQueries({ queryKey: ['orders-chat', 'summary'] });
  };
  const send = useMutation({
    mutationFn: () => api.post<ConversationDetailDTO>(`/orders-chat/conversations/${id}/messages`, { text, internal }),
    onSuccess: (d) => {
      setText('');
      refresh(d);
    },
    onError: (err) => toast.error('No se pudo mandar', errorMessage(err)),
  });
  const mode = useMutation({
    mutationFn: (m: 'take' | 'release' | 'reactivate' | 'close') =>
      api.post<ConversationDetailDTO>(`/orders-chat/conversations/${id}/mode`, { mode: m }),
    onSuccess: (d, m) => {
      refresh(d);
      if (m === 'close') {
        // Cerrada sale de la lista: se vuelve a la bandeja y se avisa dónde quedó.
        setConfirmClose(false);
        toast.success('Conversación cerrada', 'Queda en «Cerradas». Si el cliente vuelve a escribir, empieza una conversación nueva.');
        onBack();
      }
    },
    onError: (err) => toast.error('No se pudo cambiar', errorMessage(err)),
  });
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { can } = useSession();
  const remove = useMutation({
    mutationFn: () => api.del(`/orders-chat/conversations/${id}`),
    onSuccess: () => {
      setConfirmDelete(false);
      void qc.invalidateQueries({ queryKey: ['orders-chat', 'conversations'] });
      toast.success('Conversación borrada');
      onBack();
    },
    onError: (err) => toast.error('No se pudo borrar', errorMessage(err)),
  });

  const d = detail.data;
  if (!d) {
    return (
      <div className="bf-chat-thread" style={{ padding: 16 }}>
        <Skeleton style={{ height: 320 }} />
      </div>
    );
  }
  const c = d.conversation;
  const queued = d.messages.some((m) => m.status === 'QUEUED' && !m.internal);

  return (
    <>
      <section className="bf-chat-thread">
        <header className="bf-chat-thread-head">
          <Button variant="ghost" size="sm" iconOnly icon={<ArrowLeft />} aria-label="Volver a la lista" className="bf-chat-back" onClick={onBack} />
          <span className="bf-chat-avatar" aria-hidden>
            {initials(c.contactName)}
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <strong>{c.contactName}</strong>
            <div className="bf-cell-sub">
              {phoneText(c.phone)} ·{' '}
              {c.channelName.toLowerCase().includes(CHANNEL_KIND_LABELS[c.channelKind].toLowerCase())
                ? c.channelName
                : `${CHANNEL_KIND_LABELS[c.channelKind]} · ${c.channelName}`}
              {c.branchName ? ` · ${c.branchName}` : ''}
            </div>
          </div>
          <Badge tone={STATUS_TONES[c.status]} dot>
            {CONVERSATION_STATUS_LABELS[c.status]}
            {c.assignedToName && c.status === 'HUMAN' ? `: ${c.assignedToName}` : ''}
          </Badge>
          <div className="bf-chat-actions">
            {c.status === 'AGENT' && (
              <Button size="sm" variant="secondary" icon={<UserCheck />} loading={mode.isPending} onClick={() => mode.mutate('take')}>
                Tomar la conversación
              </Button>
            )}
            {c.status === 'HUMAN' && (
              <Button size="sm" variant="secondary" icon={<Bot />} loading={mode.isPending} onClick={() => mode.mutate('release')}>
                Devolver al agente
              </Button>
            )}
            {c.status === 'MUTED' && (
              <Button size="sm" variant="secondary" icon={<Bot />} loading={mode.isPending} onClick={() => mode.mutate('reactivate')}>
                Reactivar
              </Button>
            )}
            {(c.status !== 'CLOSED' || can('settings.manage')) && (
              <Menu>
                <MenuTrigger asChild>
                  <button type="button" className="bf-icon-btn" aria-label="Más acciones de la conversación">
                    <MoreHorizontal size={18} aria-hidden />
                  </button>
                </MenuTrigger>
                <MenuContent>
                  {c.status !== 'CLOSED' ? (
                    <MenuItem icon={<X />} onSelect={() => setConfirmClose(true)}>
                      Cerrar conversación
                    </MenuItem>
                  ) : (
                    <MenuItem icon={<Trash2 />} onSelect={() => setConfirmDelete(true)}>
                      Borrar conversación
                    </MenuItem>
                  )}
                </MenuContent>
              </Menu>
            )}
          </div>
          <Dialog
            open={confirmDelete}
            onOpenChange={(o) => !remove.isPending && setConfirmDelete(o)}
            title="¿Borrar esta conversación?"
            description="Se borran todos sus mensajes. Los pedidos no se borran. No se puede deshacer."
          >
            <div className="bf-form-actions">
              <Button onClick={() => setConfirmDelete(false)} disabled={remove.isPending}>
                Cancelar
              </Button>
              <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
                Borrar conversación
              </Button>
            </div>
          </Dialog>
          <Dialog
            open={confirmClose}
            onOpenChange={(o) => !mode.isPending && setConfirmClose(o)}
            title="¿Cerrar esta conversación?"
            description="Sale de la bandeja y queda en «Cerradas», con todos sus mensajes y pedidos. Si el cliente vuelve a escribir, empieza una conversación nueva desde cero."
          >
            <div className="bf-form-actions">
              <Button onClick={() => setConfirmClose(false)} disabled={mode.isPending}>
                Cancelar
              </Button>
              <Button variant="primary" loading={mode.isPending} onClick={() => mode.mutate('close')}>
                Cerrar conversación
              </Button>
            </div>
          </Dialog>
        </header>
        <div className="bf-chat-pane">
          <div
            ref={msgs}
            className="bf-chat-messages"
            aria-live="polite"
            onScroll={(e) => {
              const el = e.currentTarget;
              atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
              if (atBottom.current && unseen) setUnseen(false);
            }}
          >
            {c.fromAd && (
              <div className="bf-chat-system">
                <Megaphone size={13} aria-hidden /> Llegó desde un anuncio
                {c.freeUntil ? `: hasta el ${new Date(c.freeUntil).toLocaleDateString('es-AR')} Meta no cobra los mensajes de esta conversación` : ''}
                .
              </div>
            )}
            {d.messages.map((m) =>
              m.author === 'SYSTEM' && m.direction === 'IN' ? (
                <div key={m.id} className="bf-chat-system">
                  {m.body}
                </div>
              ) : (
                <div
                  key={m.id}
                  className={`bf-chat-msg is-${m.direction === 'IN' ? 'in' : 'out'}${m.internal ? ' is-note' : ''}${m.author === 'AGENT' ? ' is-agent' : ''}`}
                >
                  <div className="bf-chat-msg-meta">
                    {m.internal ? (
                      <>
                        <Lock size={12} aria-hidden /> Nota interna{m.authorName ? ` de ${m.authorName}` : ''}
                      </>
                    ) : m.author === 'AGENT' ? (
                      <>
                        <Bot size={12} aria-hidden /> Agente
                      </>
                    ) : m.author === 'STAFF' ? (
                      (m.authorName ?? 'Carnicería')
                    ) : m.author === 'SYSTEM' ? (
                      'Aviso automático'
                    ) : null}
                  </div>
                  <div className="bf-chat-msg-body">
                    {m.kind === 'AUDIO' ? (
                      <>
                        <span className="bf-cell-sub">{m.body ? '🎤 Audio (transcripto)' : '🎤 Audio'}</span>
                        {m.body && <div>{m.body}</div>}
                      </>
                    ) : (
                      (m.body ?? `[${m.kind.toLowerCase()}]`)
                    )}
                  </div>
                  <div className="bf-chat-msg-foot">
                    {shortTime(m.createdAt)} <StatusIcon m={m} />
                  </div>
                  {m.error && (
                    <div className="bf-cell-sub" style={{ color: 'var(--bf-danger)' }}>
                      {m.error}
                    </div>
                  )}
                </div>
              ),
            )}
          </div>
          {unseen && (
            <button type="button" className="bf-chat-new" onClick={toBottom}>
              Mensajes nuevos ↓
            </button>
          )}
        </div>
        {queued && <Callout>Los mensajes en cola (reloj) se envían cuando la línea de WhatsApp esté conectada.</Callout>}
        {!d.windowOpen && c.status !== 'CLOSED' && !internal && (
          <Callout tone="warning">
            Pasaron más de 24 horas desde el último mensaje del cliente: WhatsApp solo deja mandar mensajes con plantilla aprobada.
          </Callout>
        )}
        {c.status === 'CLOSED' ? (
          <div className="bf-chat-closed">Conversación cerrada. Si el cliente vuelve a escribir, empieza una conversación nueva.</div>
        ) : (
          <form
            className="bf-chat-compose"
            onSubmit={(e) => {
              e.preventDefault();
              if (text.trim()) send.mutate();
            }}
          >
            {quickOpen && (
              <ul className="bf-chat-quick" aria-label="Respuestas rápidas">
                {QUICK_REPLIES.map((r) => (
                  <li key={r}>
                    <button
                      type="button"
                      onClick={() => {
                        setText(r);
                        setQuickOpen(false);
                      }}
                    >
                      {r}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="bf-chat-compose-row">
              <Button
                type="button"
                variant="ghost"
                iconOnly
                icon={<Zap />}
                aria-label="Respuestas rápidas"
                aria-expanded={quickOpen}
                title="Respuestas rápidas"
                onClick={() => setQuickOpen(!quickOpen)}
              />
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={1}
                maxLength={2000}
                placeholder={internal ? 'Nota para el equipo (el cliente no la ve)…' : 'Escribile al cliente…'}
                aria-label="Mensaje"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (text.trim()) send.mutate();
                  }
                }}
                style={internal ? { background: 'var(--bf-warning-soft)' } : undefined}
              />
              <Button type="submit" icon={<Send />} loading={send.isPending} disabled={!text.trim()}>
                {internal ? 'Guardar nota' : 'Enviar'}
              </Button>
            </div>
            <div className="bf-chat-compose-foot">
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Switch checked={internal} onCheckedChange={setInternal} label="Nota interna" />
                Nota interna
              </label>
              {!internal && c.status === 'AGENT' && (
                <span className="bf-cell-sub">Si escribís, tomás la conversación y el agente deja de responder.</span>
              )}
            </div>
          </form>
        )}
      </section>
      <aside className={`bf-chat-side${side ? ' is-open' : ''}`} aria-label="Cliente y pedidos">
        <div className="bf-chat-rail">
          <button
            type="button"
            className="bf-chat-rail-btn"
            onClick={() => showSide(side ? null : 'cliente')}
            title={side ? 'Cerrar el panel' : 'Ver el cliente'}
            aria-label={side ? 'Cerrar el panel' : 'Ver el cliente'}
          >
            {side ? <ChevronRight size={16} aria-hidden /> : <ChevronLeft size={16} aria-hidden />}
          </button>
          <button
            type="button"
            className="bf-chat-rail-btn"
            aria-pressed={side === 'cliente'}
            onClick={() => showSide(side === 'cliente' ? null : 'cliente')}
          >
            <UserRound size={16} aria-hidden />
            <span className="bf-chat-rail-label">Cliente</span>
          </button>
          <button
            type="button"
            className="bf-chat-rail-btn"
            aria-pressed={side === 'pedidos'}
            onClick={() => showSide(side === 'pedidos' ? null : 'pedidos')}
          >
            <ClipboardList size={16} aria-hidden />
            <span className="bf-chat-rail-label">Pedidos</span>
            {d.orders.length > 0 && <span className="bf-chat-rail-count">{d.orders.length}</span>}
          </button>
        </div>
        {side && (
          <div className="bf-chat-side-body">
            {side === 'cliente' && (
              <section>
                <p className="bf-label" style={{ marginBottom: 6 }}>
                  Cliente
                </p>
                {d.customer ? (
                  <>
                    <Link to={`/clientes/${d.customer.id}`} className="bf-cell-main">
                      {d.customer.name}
                    </Link>
                    <div className="bf-cell-sub">{d.customer.phone ?? phoneText(c.phone)}</div>
                    {d.customer.address && <div className="bf-cell-sub">📍 {d.customer.address}</div>}
                    {d.customer.balanceCents > 0 && (
                      <div style={{ marginTop: 6 }}>
                        <Badge tone="warning">Debe {formatMoney(d.customer.balanceCents)}</Badge>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="bf-cell-main">{c.contactName}</div>
                    <div className="bf-cell-sub">{phoneText(c.phone)}</div>
                    <p className="bf-cell-sub" style={{ marginTop: 6 }}>
                      Todavía no es cliente: se da de alta solo con su primer pedido.
                    </p>
                  </>
                )}
                {/* Para los envíos: la dirección del último pedido con envío, si es distinta de la guardada. */}
                {(() => {
                  const last = d.orders.find((o) => o.fulfillment === 'DELIVERY' && o.address);
                  if (!last || last.address === d.customer?.address) return null;
                  return (
                    <div style={{ marginTop: 10 }}>
                      <p className="bf-label" style={{ marginBottom: 4 }}>
                        Dirección de envío
                      </p>
                      <div className="bf-cell-sub">
                        📍 {last.address}
                        {last.zone ? ` · zona ${last.zone}` : ''}
                      </div>
                    </div>
                  );
                })()}
                {c.status === 'MUTED' && (
                  <p className="bf-cell-sub" style={{ marginTop: 10 }}>
                    <VolumeX size={13} aria-hidden /> El agente la silenció por bromas o mal uso.
                  </p>
                )}
              </section>
            )}
            {side === 'pedidos' && (
              <section>
                <p className="bf-label" style={{ marginBottom: 6 }}>
                  Pedidos
                </p>
                {d.orders.length === 0 && <p className="bf-cell-sub">Sin pedidos todavía.</p>}
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
                  {d.orders.map((o) => (
                    <li key={o.id}>
                      <Link to={`/pedidos?p=${o.id}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                        <span>
                          <strong>{o.number ? `#${o.number}` : 'En armado'}</strong>{' '}
                          <span className="bf-cell-sub">{formatMoney(o.finalTotalCents ?? o.estimatedTotalCents)}</span>
                        </span>
                        <Badge tone={ORDER_TONES[o.status]}>{orderLabel(o.status)}</Badge>
                      </Link>
                      <div className="bf-cell-sub">
                        {o.fulfillment === 'DELIVERY'
                          ? `🛵 Envío a ${o.address ?? '(sin dirección)'}${o.zone ? ` · ${o.zone}` : ''}`
                          : '🏪 Retira en el local'}
                      </div>
                      {(o.scheduledFor || o.paymentMethod) && (
                        <div className="bf-cell-sub">
                          {o.scheduledFor ? formatDateTime(o.scheduledFor) : ''}
                          {o.scheduledFor && o.paymentMethod ? ' · ' : ''}
                          {o.paymentMethod ? PAYMENT_OPTION_LABELS[o.paymentMethod] : ''}
                          {o.paidAt ? ' · pagado' : ''}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </aside>
    </>
  );
}

/** De dónde viene la conversación (hoy WhatsApp; después Instagram y otros). */
function ChannelIcon({ kind }: { kind: ConversationRowDTO['channelKind'] }) {
  return (
    <span className={`bf-chat-channel is-${kind.toLowerCase()}`} title={CHANNEL_KIND_LABELS[kind]}>
      <svg viewBox="0 0 24 24" width="11" height="11" aria-hidden="true" fill="currentColor">
        <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2a8.2 8.2 0 0 1-4.2-1.1l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8s-.4-.1-.6.1-.7.8-.8 1-.3.2-.6.1a6.7 6.7 0 0 1-3.3-2.9c-.2-.4.2-.4.7-1.3a.4.4 0 0 0 0-.4l-.8-1.9c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.8 11.9 11.9 0 0 0 4.6 4c1.7.7 2.4.8 3.2.7a2.8 2.8 0 0 0 1.8-1.3 2.3 2.3 0 0 0 .2-1.3c-.1-.1-.3-.2-.6-.3Z" />
      </svg>
    </span>
  );
}
